import type {
  OrderSource,
  OrderStatus,
  PaymentMethod,
  PickupMethod,
} from "@/types";

export interface OrderStatusStyle {
  label: string;
  className: string;
  dot: string;
}

export const ORDER_STATUS_CONFIG: Record<OrderStatus, OrderStatusStyle> = {
  new: {
    label: "جديد",
    className: "bg-primary-100 text-primary-800",
    dot: "bg-primary-500",
  },
  preparing: {
    label: "جارٍ التحضير",
    className: "bg-accent-100 text-accent-900",
    dot: "bg-accent-500",
  },
  almost: {
    label: "قارب على الانتهاء",
    className: "bg-accent-200 text-accent-900",
    dot: "bg-accent-600",
  },
  ready: {
    label: "جاهز للاستلام",
    className: "bg-secondary-200 text-secondary-900",
    dot: "bg-secondary-600",
  },
  outForDelivery: {
    label: "خرج للتوصيل",
    className: "bg-accent-200 text-accent-900",
    dot: "bg-accent-600",
  },
  received: {
    label: "مُستلَم",
    className: "bg-primary-200 text-primary-900",
    dot: "bg-primary-700",
  },
  missed: {
    label: "لم يُستلم",
    className: "bg-red-100 text-red-700",
    dot: "bg-red-500",
  },
};

// Delivery orders follow their own labels so no pickup wording leaks into them.
// «جاهز للاستلام / مُستلَم / لم يُستلم» are never shown inside a delivery order.
export const DELIVERY_STATUS_CONFIG: Partial<
  Record<OrderStatus, OrderStatusStyle>
> = {
  ready: {
    label: "جاهز للتوصيل",
    className: "bg-secondary-200 text-secondary-900",
    dot: "bg-secondary-600",
  },
  outForDelivery: {
    label: "خرج للتوصيل",
    className: "bg-accent-200 text-accent-900",
    dot: "bg-accent-600",
  },
  received: {
    label: "تم التسليم",
    className: "bg-primary-200 text-primary-900",
    dot: "bg-primary-700",
  },
};

// Resolves the visual config for a status, using delivery-specific labels when
// the order is a «توصيل بواسطة المحل» order.
export function getOrderStatusConfig(
  status: OrderStatus,
  pickup?: PickupMethod,
): OrderStatusStyle {
  if (pickup === "delivery" && DELIVERY_STATUS_CONFIG[status]) {
    return DELIVERY_STATUS_CONFIG[status] as OrderStatusStyle;
  }
  return ORDER_STATUS_CONFIG[status];
}

export const PAYMENT_CONFIG: Record<
  PaymentMethod,
  { label: string; icon: string }
> = {
  card: { label: "بطاقة", icon: "ri-bank-card-line" },
  applepay: { label: "Apple Pay", icon: "ri-apple-fill" },
  wallet: { label: "محفظة", icon: "ri-smartphone-line" },
  cash: { label: "كاش", icon: "ri-money-dollar-box-line" },
};

export const PICKUP_CONFIG: Record<
  PickupMethod,
  { label: string; icon: string }
> = {
  branch: { label: "استلام من داخل الفرع", icon: "ri-store-3-line" },
  car: { label: "استلام من السيارة", icon: "ri-car-line" },
  local: { label: "طلب محلي داخل الفرع", icon: "ri-restaurant-2-line" },
  delivery: { label: "توصيل بواسطة المحل", icon: "ri-e-bike-2-line" },
};

export const ORDER_SOURCE_CONFIG: Record<
  OrderSource,
  { label: string; short: string; icon: string; className: string }
> = {
  app: {
    label: "طلب من تطبيق العميل",
    short: "التطبيق",
    icon: "ri-smartphone-line",
    className: "bg-accent-100 text-accent-900",
  },
  branch: {
    label: "طلب من الفرع (كاشير)",
    short: "الفرع",
    icon: "ri-store-3-line",
    className: "bg-secondary-200 text-secondary-900",
  },
};

// Fixed fee added to every order that comes from the customer app.
// Determined by the platform admin (80 halalas = 0.80 SAR) and not editable by stores.
export const DEFAULT_APP_ORDER_FEE = 0.8;

export const ORDER_STATUS_FILTERS: { key: OrderStatus | "all"; label: string }[] =
  [
    { key: "all", label: "الكل" },
    { key: "new", label: "جديد" },
    { key: "preparing", label: "جارٍ التحضير" },
    { key: "almost", label: "قارب على الانتهاء" },
    { key: "ready", label: "جاهز للاستلام" },
  ];